// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: FAQ  (route /faq — "Questions & answers", linked from the Help center and the profile page)
// Search + category chips (Orders · Cuts · Delivery · Refunds & wallet · Account & privacy) over an accordion of the answers in
// data/helpCenter.ts (every one true to what the system does or citing the published Terms/Privacy clause). The cuts answer lists the
// REAL cut names from data/cuts. Ends with the real contact channel. Open to signed-out visitors.
// Deliberately NOT here (no data/system behind it): "Certified Cold-Chain Protocols" / "Sub-Zero Guarantee" / 0–4 °C probe banner,
// "100 % preservative free", sourcing from named harbours, dock test swabs, "master fishmongers hand-carve", 60-second refunds, the
// Eco-Box ₹20 credit, the "Harbor Master" / "Sashimi grade" inspection card, "Was this helpful?" votes (nothing stores them), and the
// "typical response under 3 minutes" line.
// Neighbours: data/helpCenter, pages/HelpCenter, data/whatsappContext.
// ─────────────────────────────────────────────────────────────────────────────
import { useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ChevronDown, MessageCircle, Search } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import PageTransition from "../components/PageTransition";
import { cutsFor } from "../data/cuts";
import { FAQ_CATEGORIES, HELP_FAQS, type FaqCategory } from "../data/helpCenter";
import { SUPPORT_HOURS } from "../data/legalContact";
import { seafoodItems } from "../data/seafood";
import { getWhatsAppLink } from "../data/whatsappContext";
import useDocumentTitle from "../hooks/useDocumentTitle";

type Filter = "all" | FaqCategory;

export default function Faq() {
  useDocumentTitle("FAQ");
  const baseId = useId();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [open, setOpen] = useState<string | null>(HELP_FAQS[0].q);
  const term = query.trim().toLowerCase();

  // The distinct cut names across the whole menu, in first-seen order.
  const cutNames = useMemo(() => [...new Set(seafoodItems.flatMap((item) => cutsFor(item).map((cut) => cut.name)))], []);

  const shown = HELP_FAQS.filter((faq) => (filter === "all" || faq.category === filter) && (term === "" || `${faq.q} ${faq.a}`.toLowerCase().includes(term)));
  const countOf = (id: FaqCategory) => HELP_FAQS.filter((faq) => faq.category === id).length;
  const labelOf = (id: FaqCategory) => FAQ_CATEGORIES.find((c) => c.id === id)?.label ?? "";

  return (
    <PageTransition>
      <div className="bg-foam-50 pb-24 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs />
        <div className="mx-auto flex max-w-xl flex-col gap-4 px-4 pt-2">
          <header className="flex items-center gap-3">
            <Link to="/help" aria-label="Back to the help center" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-ocean-900 shadow-sm ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10">
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="font-display text-2xl font-bold text-ocean-950 dark:text-foam-50">Questions &amp; answers</h1>
              <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{HELP_FAQS.length} answers about ordering, cuts, delivery and refunds</p>
            </div>
          </header>

          <Link to="/coming-soon/dock-standards-faq" className="flex min-h-12 items-center justify-between gap-3 rounded-2xl bg-coral-500/10 px-4 py-3 text-sm font-bold text-ocean-950 ring-1 ring-coral-500/30 dark:text-foam-50">
            <span>FAQ &amp; dock standards</span>
            <span className="shrink-0 rounded-full bg-coral-500/20 px-2 py-0.5 text-[10px] text-coral-600 dark:text-coral-300">Coming soon</span>
          </Link>
          <div className="flex items-center rounded-xl bg-white px-3 shadow-sm ring-1 ring-ocean-900/10 dark:bg-ocean-900 dark:ring-white/10">
            <Search className="h-5 w-5 shrink-0 text-ocean-800 dark:text-foam-100" aria-hidden="true" />
            <label htmlFor={`${baseId}-q`} className="sr-only">Search the FAQ</label>
            <input
              id={`${baseId}-q`}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search cuts, refunds, delivery…"
              className="min-h-12 w-full bg-transparent px-2 text-sm text-ocean-950 outline-none placeholder:text-ocean-800/60 dark:text-foam-50"
            />
          </div>

          <div role="group" aria-label="Filter by topic" className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
            {([{ id: "all", label: "All" }, ...FAQ_CATEGORIES] as { id: Filter; label: string }[]).map((chip) => {
              const on = filter === chip.id;
              return (
                <button
                  key={chip.id}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setFilter(chip.id)}
                  className={`min-h-11 shrink-0 rounded-full px-4 text-sm font-bold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 ${on ? "bg-ocean-800 text-white shadow-sm" : "bg-white text-ocean-900 ring-1 ring-ocean-900/10 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10"}`}
                >
                  {chip.label}
                  {chip.id !== "all" && <span className="opacity-70"> ({countOf(chip.id)})</span>}
                </button>
              );
            })}
          </div>
          <p role="status" className="sr-only">{shown.length} {shown.length === 1 ? "answer" : "answers"} shown.</p>

          {shown.length === 0 && (
            <div className="rounded-2xl bg-white p-6 text-center shadow-sm dark:bg-ocean-900">
              <p className="font-display text-lg font-bold text-ocean-950 dark:text-foam-50">No answer matches that</p>
              <p className="mt-1 text-sm text-ocean-800/85 dark:text-foam-100/80">Try another word or topic, or ask us directly below.</p>
            </div>
          )}

          <ul className="flex flex-col gap-3">
            {shown.map((faq, index) => {
              const expanded = open === faq.q;
              const id = `${baseId}-${index}`;
              return (
                <li key={faq.q} className="rounded-2xl bg-white shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
                  <h2>
                    <button
                      type="button"
                      aria-expanded={expanded}
                      aria-controls={id}
                      onClick={() => setOpen(expanded ? null : faq.q)}
                      className="flex min-h-14 w-full items-start justify-between gap-3 rounded-2xl p-4 text-left focus-visible:outline-2 focus-visible:outline-teal-600"
                    >
                      <span>
                        <span className="mb-0.5 block text-[11px] font-bold uppercase tracking-wider text-teal-700 dark:text-teal-300">{labelOf(faq.category)}</span>
                        <span className="block text-sm font-bold text-ocean-950 dark:text-foam-50">{faq.q}</span>
                      </span>
                      <ChevronDown className={`mt-1 h-5 w-5 shrink-0 text-ocean-800 transition-transform motion-reduce:transition-none dark:text-foam-100 ${expanded ? "rotate-180" : ""}`} aria-hidden="true" />
                    </button>
                  </h2>
                  <div id={id} hidden={!expanded} className="px-4 pb-4 text-sm text-ocean-800/90 dark:text-foam-100/85">
                    <p className="leading-relaxed">{faq.a}</p>
                    {faq.extra === "cuts" && (
                      <>
                        <p className="mt-3 text-xs font-bold text-ocean-900 dark:text-foam-50">Cuts on the menu</p>
                        <ul className="mt-1 flex flex-wrap gap-1.5">
                          {cutNames.map((name) => (
                            <li key={name} className="rounded-full bg-ocean-700/10 px-2.5 py-1 text-xs font-semibold text-ocean-900 dark:bg-white/10 dark:text-foam-50">{name}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          <section aria-labelledby={`${baseId}-more`} className="mt-2 flex flex-col items-center rounded-2xl bg-ocean-700/5 p-5 text-center dark:bg-white/5">
            <span aria-hidden="true" className="flex h-12 w-12 items-center justify-center rounded-full bg-teal-100 text-teal-700"><MessageCircle className="h-6 w-6" /></span>
            <h2 id={`${baseId}-more`} className="mt-2 font-display text-lg font-bold text-ocean-950 dark:text-foam-50">Still have questions?</h2>
            <p className="mt-1 max-w-xs text-sm text-ocean-800/85 dark:text-foam-100/80">Can&apos;t find your answer? Message us on WhatsApp ({SUPPORT_HOURS}) or use the help center.</p>
            <a
              href={getWhatsAppLink("Hi ZenFresh, I have a question.")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-full bg-whatsapp px-6 font-bold text-ocean-950 shadow-md hover:opacity-90"
            >
              <MessageCircle className="h-5 w-5" aria-hidden="true" /> Chat on WhatsApp<span className="sr-only"> (opens in a new tab)</span>
            </a>
            <Link to="/help" className="mt-2 text-sm font-bold text-ocean-800 underline dark:text-foam-100">Open the help center</Link>
          </section>
        </div>
      </div>
    </PageTransition>
  );
}
